<script setup lang="ts">
import { computed } from 'vue';
import api from '@/api';
import {
  ContentsContext,
  Feature,
  PageName,
  SummaryInfo,
} from '@/api/tracking';
import AdpDocumentSourceBadge from '@/components/common/article/adp-document-source-badge.vue';
import CitationPill from '@/components/common/atoms/citation-pill.vue';
import {
  AdpDocument,
  DocType,
  isAdpDocument,
  isReport,
  isUserDocument,
  UserDocument,
  UserDocumentViewInfo,
} from '@/types';
import { getPublisher } from '@/utils/adpDocument';
import { useUserDocumentActionHistories } from '@/utils/composables/useUserDocumentActionHistories';
import { SURVEY_FEATURE_NAME } from '@/utils/survey/constants';
import { getPageUrl } from '@/utils/user-document/common';
import { userSession } from '@/utils/userSession';
import { useEmitter } from '@/utils/vue';

interface Props {
  badgeNumber: number;
  docType: DocType | 'user_document';
  sourceDocument: AdpDocument | UserDocument;
  pageName: PageName;
  feature: Feature;
  summaryInfo?: SummaryInfo;
  contentsContext?: ContentsContext;
}

const props = defineProps<Props>();

const emitter = useEmitter();
const { createUserDocumentViewHistories } = useUserDocumentActionHistories(
  userSession.getUserId(),
);

// AdpDocumentSourceBadge のカードは AdpDocument 専用のため、UserDocument のときは使わない
const adpDocument = computed(() =>
  isAdpDocument(props.sourceDocument)
    ? (props.sourceDocument as AdpDocument)
    : undefined,
);

const documentPublisher = computed(() => {
  if (isUserDocument(props.sourceDocument)) {
    return props.sourceDocument.last_updated_by;
  }
  return getPublisher(props.sourceDocument);
});

const documentUrl = computed(() => {
  if (isUserDocument(props.sourceDocument)) {
    const pageNumber = props.sourceDocument.page_number;
    return getPageUrl(pageNumber ?? 1, props.sourceDocument);
  }

  if (isReport(props.sourceDocument)) {
    return `${props.sourceDocument.url}#page=${props.sourceDocument.page_number ?? 1}`;
  }

  return props.sourceDocument.url;
});

// UserDocument のフォールバック表示(タイトルリンク)からのみ呼ばれる。
// AdpDocument の閲覧計測は AdpDocumentSourceBadge(カード内)側が担う。
const handleClick = async () => {
  const document = props.sourceDocument;
  if (!isUserDocument(document)) return;

  emitter.emit('article-updated', { ...document, is_read: true });
  await createUserDocumentViewHistories(document.id);
  await api.trackingUserDocumentEvent('user_document_view', {
    page: { name: SURVEY_FEATURE_NAME },
    feature: SURVEY_FEATURE_NAME,
    user_document: { id: document.id },
    contents_context: props.contentsContext,
    view_type: 'original',
  } as UserDocumentViewInfo);
};
</script>

<template>
  <span class="badge-wrapper">
    <AdpDocumentSourceBadge
      v-if="adpDocument"
      :position="props.badgeNumber"
      :adp-document="adpDocument"
      :page-name="props.pageName"
      :feature="props.feature"
      :contents-context="props.contentsContext"
      :summary-info="props.summaryInfo"
      variant="gray"
    />
    <!-- 社内ドキュメント (UserDocument) は AdpDocument カードを表示できないため publisher + タイトルリンクで表示する -->
    <VTooltip
      v-else
      placement="bottom"
      :popper-triggers="['hover', 'focus']"
      :delay="{ show: 0, hide: 100 }"
    >
      <CitationPill
        :doc-type="props.docType"
        :number="props.badgeNumber"
        variant="gray"
      />
      <template #popper>
        <div>
          <p>{{ documentPublisher }}</p>
          <a
            class="help-link"
            :href="documentUrl"
            @click="handleClick"
            target="_blank"
            rel="noopener noreferrer"
            >{{ sourceDocument.title }}</a
          >
        </div>
      </template>
    </VTooltip>
  </span>
</template>

<style scoped lang="scss">
.badge-wrapper {
  display: inline-flex;
  margin: 0 4px;
}
</style>
